import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import * as Profile from "@/components/auth/Profile";
import { FormButton } from "@/components/form/FormButton";
import { Sidebar } from "@/components/sidebar";
import * as Skeleton from "@/components/skeleton/Skeleton";
import { useStore } from "@/store";
import { twMerge } from "@/utils/twMerge";

export const ProfileData = observer(() => {
  const { auth, publicProfile, dialog } = useStore();
  const { team } = publicProfile;
  const t = useTranslations();

  if (publicProfile.teamStatus === "idle" || publicProfile.teamStatus === "loading") {
    return (
      <Skeleton.Root className="p-2.5 pt-3.75">
        <Skeleton.Block
          className={twMerge(
            "w-[100px] h-[100px] rounded-full mx-auto bg-gray-100 dark:bg-gray-900",
            "sm:w-10 sm:h-10",
          )}
        />
        <Sidebar.GroupSection className="mt-1.25 p-0">
          <Skeleton.Block className="h-[40px]" />
        </Sidebar.GroupSection>
        <Sidebar.GroupSection className="mt-1.25 p-0">
          <Skeleton.Block className="h-[40px]" />
        </Sidebar.GroupSection>
      </Skeleton.Root>
    );
  }

  if (publicProfile.teamStatus === "error" || !team) {
    return (
      <div className="w-full p-2.5 text-center text-[16px] leading-[20px] text-gray-500 font-normal">
        {publicProfile.error}
      </div>
    );
  }

  const { avatarUrls, title } = team;
  const imageSrc = avatarUrls?.[120];

  return (
    <>
      <div className="flex flex-col items-center pt-3.75 pb-2.5">
        <Profile.Userpic
          name={title || ""}
          src={imageSrc}
          setColorFrom={team.uid}
          className="w-25 h-25 text-[28px] sm:w-18 sm:h-18"
        />
        <div className="flex flex-col items-center w-full px-2.5 text-center tracking-tight">
          <h2 className="mt-1.5 text-[22px] font-semibold leading-none text-document-text">
            {team.title}
          </h2>
          <div
            className={twMerge("mt-1.5 w-full text-size-15 text-gray-500", !team.bio && "mb-3.75")}
          >
            @{team.permalink}
          </div>
          {team.bio && (
            <div className="w-full mt-1.25 mb-3.75 text-size-15 text-left text-gray-500">
              {team.bio}
            </div>
          )}
          {/* TODO: we decided to hide followers count for now */}
          {/* {team.followersCount !== null && (
            <div
              className={twMerge(
                "w-full mt-1.25 mb-2.5 text-[16px] leading-[20px] text-document-text font-normal",
                publicProfile.isOurProfile && "mb-0",
              )}
            >
              {team.followersCount}{" "}
              {t("doc.publicProfile.followers", { count: team.followersCount })}
            </div>
          )} */}
          {publicProfile.isOurProfile === false && (
            <FormButton
              variant={publicProfile.team?.following ? "outline" : "accent"}
              className="w-full h-8.5 text-[15px] tracking-tight rounded-lg"
              onClick={() => {
                if (!auth.isLoggedIn) {
                  dialog.loginDialog.open();
                  return;
                }
                publicProfile.toggleFollow();
              }}
              isBusy={auth.isLoggedIn && publicProfile.followingStatus === "loading"}
            >
              {publicProfile.team?.following
                ? t("doc.publicProfile.unfollow")
                : t("doc.publicProfile.follow")}
            </FormButton>
          )}
        </div>
      </div>
    </>
  );
});
